/**
 * ReplayComparePane — React renderer for ReplayComparePaneModel
 *
 * Displays events inside/outside a replay window.
 */

import React from 'react';
import type { ReplayComparePaneModel } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, TYPOGRAPHY, ROW_HEIGHT, SPACING } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface ReplayComparePaneProps {
  model: ReplayComparePaneModel;
  onSelectEvent?: (eventId: string) => void;
}

export const ReplayComparePane: React.FC<ReplayComparePaneProps> = ({ model, onSelectEvent }) => {
  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Replay compare error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        fontSize: TYPOGRAPHY.heading.size,
        fontWeight: TYPOGRAPHY.heading.weight,
        color: NEUTRAL.text600,
        backgroundColor: NEUTRAL.bg600,
        height: ROW_HEIGHT.compact.spanHeader,
      }}>
        <span>Replay Compare</span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.totalInWindow} in window · {model.totalOutWindow} outside
        </span>
      </div>

      {model.windowId && (
        <div style={{
          padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
          backgroundColor: NEUTRAL.bg300,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
          fontSize: TYPOGRAPHY.caption.size,
          color: NEUTRAL.text400,
        }}>
          <span style={{ fontFamily: TYPOGRAPHY.mono.family }}>
            {model.windowStart?.slice(11, 23)} → {model.windowEnd?.slice(11, 23)}
          </span>
          <span style={{ marginLeft: SPACING.sp3, color: NEUTRAL.text300 }}>
            {model.disclaimer}
          </span>
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {model.rows.map((row) => {
          return (
            <div
              key={row.eventId}
              style={{
                display: 'flex',
                alignItems: 'center',
                height: ROW_HEIGHT.compact.tableRow,
                padding: `0 ${SPACING.sp3}px`,
                fontSize: TYPOGRAPHY.body.size,
                color: NEUTRAL.text500,
                borderLeft: `2px solid ${row.isSelected ? NEUTRAL.borderStrong : 'transparent'}`,
                backgroundColor: row.inWindow ? 'rgba(10, 14, 22, 0.5)' : 'transparent',
                cursor: 'pointer',
                transition: 'background-color 80ms ease-out',
              }}
              onClick={() => onSelectEvent?.(row.eventId)}
            >
              <span style={{
                fontFamily: TYPOGRAPHY.mono.family,
                fontSize: TYPOGRAPHY.mono.size,
                color: NEUTRAL.text300,
                minWidth: 72,
                marginRight: SPACING.sp3,
              }}>
                {row.timestamp.slice(11, 23)}
              </span>
              <span style={{
                width: 48,
                fontSize: TYPOGRAPHY.micro.size,
                color: row.inWindow ? '#5878a8' : NEUTRAL.text200,
                marginRight: SPACING.sp3,
              }}>
                {row.inWindow ? 'IN' : 'OUT'}
              </span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.label}
              </span>
            </div>
          );
        })}
        {model.rows.length === 0 && (
          <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' }}>
            No replay data
          </div>
        )}
      </div>
    </div>
  );
};
